import React, { FC, useContext, useState } from 'react';
import { Box, Flex, Heading, Image, Text } from '@resideo/blueprint-react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Collapsible from 'react-collapsible';
import AddPromoCode from 'components/common/AddPromoCode';
import ShippingOrderItem from './shipping/ShippingOrderItem';
import { downArrowIcon, upArrowIcon } from 'assets/images';
import { convertMoneyAmount } from 'routes/ServicesAndSubscriptions/Cart';
import { CartContext } from 'routes/ServicesAndSubscriptions/CartProvider';

const StyledCursorBox = styled(Box)`
  cursor: pointer;
  border: 2px solid #e9eaed;
  border-radius: 15px;
  padding: 24px 16px;
`;

interface OrderSummaryMobileProps {
  values: any;
}

const OrderSummaryMobile: FC<OrderSummaryMobileProps> = ({ values }) => {
  const { t } = useTranslation();
  const [orderCollapse, setOrderCollapse] = useState(false);
  const { state } = useContext(CartContext);

  const shippingTotal = values?.shippingMethod === 'standard' ? 10 : 20;

  const hasProduct =
    state?.cartItems?.filter(f => f.type === 'product').length > 0;

  const calculateTax = () => {
    // only include shipping cost in the tax calculation if there is
    // a physical product to be shipped.
    const shippingTotal = hasProduct
      ? values?.shippingMethod === 'standard'
        ? 10
        : 20
      : 0;
    const correctTaxAmount =
      (+state?.order?.subTotal?.amount + shippingTotal) *
      +state?.order?.taxRate;

    return correctTaxAmount;
  };

  const calculateTotal =
    +state?.order?.subTotal?.amount + shippingTotal + calculateTax();

  return (
    <Box display={['block', 'block', 'block', 'none']} paddingBottom='1rem'>
      <Collapsible
        open={orderCollapse}
        onOpening={() => setOrderCollapse(true)}
        onClosing={() => setOrderCollapse(false)}
        trigger={
          <StyledCursorBox
            display='flex'
            justifyContent='space-between'
            alignItems='center'>
            <Box
              display='flex'
              alignItems='center'
              justifyContent='space-between'
              width='100%'>
              <Heading
                letterSpacing='normal'
                fontWeight='500'
                fontSize={'medium'}
                color='textLight3'>
                View Order Summary
              </Heading>
              <Heading
                letterSpacing='normal'
                fontWeight='500'
                fontSize={'medium'}
                color='textLight3'>
                {convertMoneyAmount(
                  {
                    amount: state.order.total.amount,
                    currencyCode: state.order.currency,
                  },
                  'en-us'
                )}
              </Heading>
            </Box>

            <Image
              src={orderCollapse ? upArrowIcon : downArrowIcon}
              alt='down-arrow'
              paddingLeft={['small', 'small', 'medium', 'medium']}
            />
          </StyledCursorBox>
        }>
        <Box paddingTop='large'>
          <Box
            display={['block', 'block', 'flex', 'flex']}
            justifyContent='space-between'
            marginBottom='medium'>
            <Box>
              <Heading
                color='textHeading'
                letterSpacing='normal'
                fontSize='1.125rem'
                fontWeight='bold'>
                {t('cart.shipping.orderSummary')}
              </Heading>
              <Text
                color='summaryParagraph'
                fontSize='small'
                fontWeight='bold'
                paddingY='small'>
                REF # {state?.order?.id}
              </Text>
            </Box>
            <Box
              display='flex'
              justifyContent='flex-end'
              alignItems='center'
              flex={1}>
              <AddPromoCode />
            </Box>
            <Box></Box>
          </Box>
          {state.order.lineItems &&
            state.order.lineItems.map(ele => (
              <React.Fragment key={ele.sku}>
                <ShippingOrderItem quantity data={ele} />
              </React.Fragment>
            ))}
          <Box marginBottom='medium'>
            {hasProduct && (
              <>
                <Flex flex='1' paddingBottom='small'>
                  <Box flex='75%'>
                    <Text color='textLight3' textAlign='right'>
                      {t('subtotalToday')}
                    </Text>
                  </Box>
                  <Box flex='25%'>
                    <Text color='textLight3' textAlign='right'>
                      {convertMoneyAmount(
                        {
                          amount: state.order.subTotal.amount,
                          currencyCode: state.order.currency,
                        },
                        'en-us'
                      )}
                    </Text>
                  </Box>
                </Flex>
                <Flex flex='1' paddingBottom='small'>
                  <Box flex='75%'>
                    <Text textAlign='right'>{t('cart.shipping.shipping')}</Text>
                  </Box>
                  <Box flex='25%'>
                    <Text textAlign='right'>
                      {convertMoneyAmount(
                        {
                          amount: shippingTotal,
                          currencyCode: state.order.currency,
                        },
                        'en-us'
                      )}
                    </Text>
                  </Box>
                </Flex>
              </>
            )}
            <Flex flex='1' paddingBottom='medium'>
              <Box flex='75%'>
                <Text textAlign='right'>{t('tax')}</Text>
              </Box>
              <Box flex='25%'>
                <Text textAlign='right'>
                  {convertMoneyAmount(
                    {
                      amount: calculateTax(),
                      currencyCode: state.order.currency,
                    },
                    'en-us'
                  )}
                </Text>
              </Box>
            </Flex>
            <Flex flex='1' paddingBottom='0'>
              <Box flex='75%'>
                <Heading
                  color='textLight3'
                  letterSpacing='normal'
                  fontSize='large'
                  textAlign='right'>
                  {t('totalToday')}
                </Heading>
              </Box>
              <Box flex='25%'>
                <Heading
                  color='textLight3'
                  letterSpacing='normal'
                  fontSize='large'
                  textAlign='right'>
                  {convertMoneyAmount(
                    {
                      amount: calculateTotal,
                      currencyCode: state.order.currency,
                    },
                    'en-us'
                  )}
                </Heading>
              </Box>
            </Flex>
          </Box>
        </Box>
      </Collapsible>
    </Box>
  );
};

export default OrderSummaryMobile;
